import { useCallback, useEffect, useMemo, useState } from "react";
import { useBlocker, useParams, type BlockerFunction } from "react-router-dom";
import { fetchExternalServices } from "@/api/externalServices";
import { AdminMenuSelect } from "@/components/admin/AdminMenuSelect";
import { IconBuilding, IconCode, IconList, IconSave } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { AdminShell, SwitchControl } from "@/components/admin/AdminShell";
import { FormPublicLinkShare } from "@/components/admin/FormPublicLinkShare";
import { InlineEditable } from "@/components/inline/InlineEditable";
import { useFormThemes } from "@/hooks/useFormThemes";
import { useAdminWorkspace } from "@/contexts/AdminWorkspaceProvider";
import { isGlobalManager, type FormSource } from "@/lib/forms/formSource";
import type { ExternalServiceDto } from "@/types/externalService";
import type { ValuesByName } from "@/types/visibility";
import { AppearanceEditor } from "./AppearanceEditor";
import { FormBuilderTabBar } from "./FormBuilderTabBar";
import { FormBuilderAlerts } from "./FormBuilderAlerts";
import { FormBuilderFormTab } from "./FormBuilderFormTab";
import { FormBuilderSettings } from "./FormBuilderSettings";
import { FormBuilderSourceSection } from "./FormBuilderSourceSection";
import { FormPreview } from "./FormPreview";
import { FormSubmissions } from "./FormSubmissions";
import { SubmitHooksEditor } from "./SubmitHooksEditor";
import { UnsavedChangesDialog } from "./UnsavedChangesDialog";
import { useFormBuilder } from "./useFormBuilder";

export default function FormBuilderPage() {
  const { formId } = useParams();
  const { catalog: themesCatalog } = useFormThemes();
  const {
    organisations,
    selectedOrganisationId,
    isUnassignedSelected,
    isServiceFormsSelected,
    selectOrganisation,
  } = useAdminWorkspace();

  const builder = useFormBuilder({ formId });
  const [externalServices, setExternalServices] = useState<
    ExternalServiceDto[]
  >([]);
  const [isLoadingExternalServices, setLoadingExternalServices] =
    useState(false);
  const [previewValuesByName, setPreviewValuesByName] = useState<ValuesByName>(
    {},
  );

  const {
    isNew,
    numericId,
    draft,
    isLoading,
    isSaving,
    isDuplicating,
    isDeleting,
    isDirty,
    skipNavigationGuardRef,
    loadError,
    saveError,
    saveFailed,
    setSaveError,
    validationErrors,
    activeTab,
    setActiveTab,
    isYandex,
    canManage,
    hooksCount,
    showPrivacySuggestion,
    detectedPersonalDataTypes,
    isGenerating,
    generateError,
    createSource,
    setCreateSource,
    formMeta,
    setOrganisationId,
    setExternalServiceId,
    fieldTypes,
    roles,
    updateDraft,
    updateSection,
    updateField,
    addField,
    removeField,
    moveField,
    moveFieldToSection,
    duplicateField,
    moveSection,
    addSection,
    removeSection,
    handleSave,
    allFieldNames,
    allBuilderFields,
    formFields,
    fioFieldNames,
    handleDuplicateForm,
    handleDeleteForm,
    handleAddPrivacyField,
    handleGenerateFields,
    dismissPrivacySuggestion,
  } = builder;

  const handleSourceChange = (source: FormSource) => {
    setCreateSource(source);
    if (
      source === "yandex" &&
      (activeTab === "appearance" || activeTab === "hooks")
    ) {
      setActiveTab("form");
    }
  };

  const preferredOrganisationId =
    isUnassignedSelected || isServiceFormsSelected
      ? null
      : (selectedOrganisationId ??
        (organisations.length === 1 ? organisations[0]!.id : null));

  useEffect(() => {
    if (
      isNew &&
      formMeta.organisationId == null &&
      preferredOrganisationId != null
    ) {
      setOrganisationId(preferredOrganisationId, { markDirty: false });
    }
  }, [
    formMeta.organisationId,
    isNew,
    preferredOrganisationId,
    setOrganisationId,
  ]);

  useEffect(() => {
    if (!isNew || !isServiceFormsSelected) return;

    let cancelled = false;
    setLoadingExternalServices(true);

    void fetchExternalServices()
      .then((services) => {
        if (!cancelled) {
          setExternalServices(
            [...services].sort((a, b) => a.name.localeCompare(b.name, "ru")),
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSaveError("Не удалось загрузить список внешних сервисов");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingExternalServices(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isNew, isServiceFormsSelected, setSaveError]);

  useEffect(() => {
    const firstService = externalServices[0];
    if (
      isNew &&
      isServiceFormsSelected &&
      formMeta.externalServiceId == null &&
      firstService
    ) {
      setExternalServiceId(firstService.id, { markDirty: false });
    }
  }, [
    externalServices,
    formMeta.externalServiceId,
    isNew,
    isServiceFormsSelected,
    setExternalServiceId,
  ]);

  const organisationOptions = useMemo(
    () => [
      ...(isGlobalManager(roles)
        ? [
            {
              value: "unassigned",
              label: "Без организации",
              icon: <IconList size={15} />,
            },
          ]
        : []),
      ...organisations.map((organisation) => ({
        value: String(organisation.id),
        label: organisation.name,
        icon: <IconBuilding size={15} />,
      })),
    ],
    [organisations, roles],
  );

  const externalServiceOptions = useMemo(
    () =>
      externalServices.map((service) => ({
        value: String(service.id),
        label: service.name,
        icon: <IconCode size={15} />,
      })),
    [externalServices],
  );

  const saveForm = async (
    options: { navigateAfterCreate?: boolean } = {},
  ): Promise<boolean> => {
    if (isNew && isServiceFormsSelected && formMeta.externalServiceId == null) {
      setSaveError(
        isLoadingExternalServices
          ? "Дождитесь загрузки внешних сервисов"
          : "Для сервисной формы нужно выбрать внешний сервис",
      );
      return false;
    }
    return handleSave(options);
  };

  const handleSaveClick = () => {
    void saveForm();
  };

  const shouldBlockNavigation = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) => {
      if (!isDirty || skipNavigationGuardRef.current) return false;
      if (nextLocation.pathname === "/login") return false;
      return (
        `${currentLocation.pathname}${currentLocation.search}${currentLocation.hash}` !==
        `${nextLocation.pathname}${nextLocation.search}${nextLocation.hash}`
      );
    },
    [isDirty, skipNavigationGuardRef],
  );
  const blocker = useBlocker(shouldBlockNavigation);

  useEffect(() => {
    if (!isDirty) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  const saveAndLeave = async () => {
    const saved = await saveForm({ navigateAfterCreate: false });
    if (blocker.state !== "blocked") return;
    if (saved) blocker.proceed();
    else blocker.reset();
  };

  return (
    <AdminShell
      compact
      wide
      hideOrganisationsNav
      backLink={{ to: "/", label: "К формам" }}
      title={
        !isLoading ? (
          <InlineEditable
            as="h1"
            plainEdit
            value={draft.name}
            placeholder={isNew ? "Новая форма" : "Название формы"}
            onChange={(name) => updateDraft({ name })}
            className="block font-unbounded text-xl font-extrabold text-ink sm:text-2xl"
            inputClassName="block w-full max-w-xl font-unbounded text-xl font-extrabold text-ink sm:text-2xl"
            emptyClassName="font-unbounded text-xl font-extrabold text-slate-400 sm:text-2xl"
          />
        ) : (
          <h1 className="font-unbounded text-xl font-extrabold text-ink sm:text-2xl">
            {isNew ? "Новая форма" : "Конструктор формы"}
          </h1>
        )
      }
      headerBelow={
        !isLoading ? (
          <InlineEditable
            as="p"
            multiline
            plainEdit
            value={draft.description}
            placeholder="Добавить описание (необязательно)"
            onChange={(description) => updateDraft({ description })}
            className="block max-w-2xl font-onest text-sm leading-relaxed text-slate-600"
            inputClassName="w-full max-w-2xl text-sm"
            emptyClassName="text-sm text-slate-400"
          />
        ) : undefined
      }
      actions={
        <>
          {!isNew && numericId && !isYandex ? (
            <FormPublicLinkShare
              formId={numericId}
              publicId={formMeta.publicId}
              onCopyError={setSaveError}
            />
          ) : null}
          {!isNew && isYandex && formMeta.publicUrl ? (
            <FormPublicLinkShare
              url={formMeta.publicUrl}
              popoverTitle="Ссылка на форму Яндекса"
              onCopyError={setSaveError}
            />
          ) : null}
          {!isLoading ? (
            <div className="flex items-center gap-2 border-l border-slate-200 pl-2.5 sm:gap-2.5 sm:pl-3">
              <label
                htmlFor="form-published"
                className="cursor-pointer select-none whitespace-nowrap font-onest text-sm font-medium text-slate-600"
              >
                Опубликовано
              </label>
              <SwitchControl
                id="form-published"
                compact
                checked={!draft.isClosed}
                onChange={(published) => updateDraft({ isClosed: !published })}
                ariaLabel={
                  draft.isClosed
                    ? "Форма не опубликована"
                    : "Форма опубликована"
                }
                checkedClassName="bg-emerald-500"
                uncheckedClassName="bg-slate-300"
              />
            </div>
          ) : null}
          <Button
            disabled={isSaving || isLoading || !canManage}
            className={`!px-4 !py-2 text-sm ${
              saveFailed
                ? "!bg-red-600 !shadow-red-600/25 hover:!bg-red-700"
                : ""
            }`}
            onClick={handleSaveClick}
            aria-describedby={saveFailed ? "form-builder-alerts" : undefined}
          >
            <IconSave size={15} />
            {isSaving
              ? "Сохранение…"
              : saveFailed
                ? "Не сохранено"
                : "Сохранить"}
          </Button>
          {isNew && organisationOptions.length > 0 ? (
            <AdminMenuSelect
              variant="link"
              ariaLabel="Организация формы"
              value={
                formMeta.organisationId != null
                  ? String(formMeta.organisationId)
                  : "unassigned"
              }
              options={organisationOptions}
              onChange={(value) => {
                const organisationId =
                  value === "unassigned" ? null : Number(value);
                setOrganisationId(organisationId);
                if (!isServiceFormsSelected) {
                  selectOrganisation(organisationId);
                }
              }}
            />
          ) : null}
          {isNew && isServiceFormsSelected ? (
            <AdminMenuSelect
              variant="link"
              ariaLabel="Внешний сервис формы"
              value={
                formMeta.externalServiceId != null
                  ? String(formMeta.externalServiceId)
                  : ""
              }
              options={externalServiceOptions}
              placeholder={
                isLoadingExternalServices ? "Загрузка сервиса…" : "Сервис"
              }
              onChange={(value) => {
                setExternalServiceId(Number(value));
                setSaveError(null);
              }}
            />
          ) : null}
        </>
      }
    >
      {loadError ? (
        <div
          className="rounded-xl border border-red-200 bg-red-50 px-4 py-3"
          role="alert"
        >
          <p className="font-onest text-sm text-red-700">{loadError}</p>
        </div>
      ) : null}

      {isLoading ? (
        <div className="animate-pulse space-y-3 rounded-xl border border-slate-200 bg-white p-4">
          <div className="h-8 rounded-lg bg-slate-100" />
          <div className="h-16 rounded-lg bg-slate-100" />
          <div className="h-28 rounded-lg bg-slate-100" />
        </div>
      ) : (
        <div className="space-y-4">
          <FormBuilderTabBar
            active={activeTab}
            hooksCount={hooksCount}
            showSubmissions={!isNew && numericId != null}
            isYandex={isYandex}
            onChange={setActiveTab}
            showDuplicate={!isNew && !isYandex && numericId != null}
            isDuplicating={isDuplicating}
            isSaving={isSaving}
            isDeleting={isDeleting}
            canManage={canManage}
            onDuplicate={handleDuplicateForm}
            trailing={
              isNew ? (
                <FormBuilderSourceSection
                  createSource={createSource}
                  onSourceChange={handleSourceChange}
                />
              ) : null
            }
          />

          <FormBuilderAlerts
            validationErrors={validationErrors}
            saveError={saveError}
            saveFailed={saveFailed}
          />

          {activeTab === "form" ? (
            <FormBuilderFormTab
              draft={draft}
              numericId={numericId}
              publicId={formMeta.publicId}
              isNew={isNew}
              isYandex={isYandex}
              canManage={canManage}
              fieldTypes={fieldTypes}
              themesCatalog={themesCatalog}
              isGenerating={isGenerating}
              generateError={generateError}
              showPrivacySuggestion={showPrivacySuggestion}
              detectedPersonalDataTypes={detectedPersonalDataTypes}
              allFieldNames={allFieldNames}
              allBuilderFields={allBuilderFields}
              previewValuesByName={previewValuesByName}
              onPreviewFieldValueChange={(fieldName, value) =>
                setPreviewValuesByName((current) => ({
                  ...current,
                  [fieldName]: value,
                }))
              }
              onUpdateSection={updateSection}
              onUpdateField={updateField}
              onAddField={addField}
              onRemoveField={removeField}
              onMoveField={moveField}
              onDropField={moveFieldToSection}
              onDuplicateField={duplicateField}
              onMoveSection={moveSection}
              onAddSection={addSection}
              onRemoveSection={removeSection}
              onUpdateDraft={updateDraft}
              onGenerateFields={handleGenerateFields}
              onAddPrivacyField={handleAddPrivacyField}
              onDismissPrivacySuggestion={dismissPrivacySuggestion}
            />
          ) : activeTab === "appearance" ? (
            <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_18rem]">
              <AppearanceEditor
                draft={draft}
                catalog={themesCatalog}
                onChange={(patch) => updateDraft(patch)}
              />
              <FormPreview
                draft={draft}
                themesCatalog={themesCatalog}
                valuesByName={previewValuesByName}
                onFieldValueChange={(fieldName, value) =>
                  setPreviewValuesByName((current) => ({
                    ...current,
                    [fieldName]: value,
                  }))
                }
                formId={numericId}
                publicId={formMeta.publicId}
              />
            </div>
          ) : activeTab === "hooks" ? (
            <SubmitHooksEditor
              value={draft.submitHooksText}
              formFields={formFields}
              fioFieldNames={fioFieldNames}
              onChange={(submitHooksText) => updateDraft({ submitHooksText })}
            />
          ) : activeTab === "submissions" && numericId ? (
            <FormSubmissions
              formId={numericId}
              draft={draft}
              source={formMeta.source}
              canManage={canManage}
            />
          ) : activeTab === "settings" ? (
            <FormBuilderSettings
              isNew={isNew}
              isYandex={isYandex}
              formId={numericId}
              formMeta={formMeta}
              draft={draft}
              fieldTypes={fieldTypes}
              organisationId={formMeta.organisationId}
              roles={roles}
              canManage={canManage}
              isSaving={isSaving}
              isDeleting={isDeleting}
              onOrganisationChange={setOrganisationId}
              onExternalServiceChange={setExternalServiceId}
              onDraftChange={updateDraft}
              onDelete={handleDeleteForm}
              onCopyError={setSaveError}
            />
          ) : null}
        </div>
      )}
      {blocker.state === "blocked" ? (
        <UnsavedChangesDialog
          isSaving={isSaving}
          onSave={() => void saveAndLeave()}
          onDiscard={blocker.proceed}
          onCancel={blocker.reset}
        />
      ) : null}
    </AdminShell>
  );
}
